// paex-edd.js — Store Availability UX v2 delivery-date engine (no UI).
// Provider seam (Sasha 2026-07-26): v1 computes everything from merchant
// settings (dispatch cutoff evaluated in the WAREHOUSE timezone, dispatch
// days, holiday list, province→business-days transit table). A future
// AfterShip App-Proxy provider can replace paexEddEstimate() without touching
// any consumer — the API key itself must NEVER ship in theme code.
// Re-execution-safe (var + typeof guards): PDP block and future card/cart
// consumers may each emit a script tag for this file.

// config = {
//   cutoff: "12:00", tz: "America/Toronto", days: "1,2,3,4,5",
//   holidays: "YYYY-MM-DD\n…", transit: "QC|2\nON|3\n*|6",
//   processing: 0
// }  — all raw setting strings; parsing is tolerant of blanks/junk lines.
// processing (Sasha 2026-09-21) = business days between the order and the
// carrier pickup, resolved per product by snippets/paex-processing-days.liquid
// (metafield → type/tag rule → theme default). AfterShip's prediction API is
// request-driven and never reads the account's order-processing rules, so
// this value is also sent to the proxy (processing_days) for live dates.

function paexEddParseConfig(config) {
  var days = String(config.days || '1,2,3,4,5')
    .split(',')
    .map(function (part) { return parseInt(part, 10); })
    .filter(function (day) { return day >= 0 && day <= 6; });
  var holidays = {};
  String(config.holidays || '')
    .split(/\r?\n/)
    .forEach(function (line) {
      var date = line.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) holidays[date] = true;
    });
  var transit = {};
  String(config.transit || '')
    .split(/\r?\n/)
    .forEach(function (line) {
      var parts = line.split('|');
      if (parts.length !== 2) return;
      var region = parts[0].trim().toUpperCase();
      var duration = parseInt(parts[1], 10);
      if (region && duration >= 0) transit[region] = duration;
    });
  var cutoff = /^\d{1,2}:\d{2}$/.test(String(config.cutoff || '').trim())
    ? String(config.cutoff).trim()
    : '12:00';
  // processing days: junk/blank → 0, capped at 30 (same bound as the worker)
  var processing = parseInt(config.processing, 10);
  if (!(processing >= 0)) processing = 0;
  if (processing > 30) processing = 30;
  return {
    cutoff: cutoff,
    tz: String(config.tz || 'America/Toronto').trim() || 'America/Toronto',
    days: days.length ? days : [1, 2, 3, 4, 5],
    holidays: holidays,
    transit: transit,
    processing: processing,
  };
}

// "Now" as seen by the warehouse: Intl re-renders the current instant in the
// warehouse timezone; the parts are recombined into plain Y/M/D/H/M numbers.
// Never trust the browser's local clock for the cutoff (a BC shopper at 10am
// local is already past a 12:00 Eastern cutoff).
function paexEddWarehouseNow(tz) {
  try {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, weekday: 'short',
    }).formatToParts(new Date());
    var map = {};
    parts.forEach(function (part) { map[part.type] = part.value; });
    var weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return {
      y: Number(map.year),
      m: Number(map.month),
      d: Number(map.day),
      hour: Number(map.hour) % 24,
      minute: Number(map.minute),
      second: Number(map.second) || 0,
      weekday: weekdays[map.weekday] != null ? weekdays[map.weekday] : new Date().getDay(),
    };
  } catch (error) {
    var now = new Date();
    return {
      y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate(),
      hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds(),
      weekday: now.getDay(),
    };
  }
}

// Milliseconds left before the dispatch cutoff, to the SECOND (Halloween clock
// 2026-10-09 — live countdowns tick every second). bufferMinutes ends the
// countdown early so checkout can finish before the real cutoff; 0 when the
// warehouse no longer takes orders for today (weekend, holiday, past cutoff).
function paexEddMsToCutoff(config, bufferMinutes) {
  var parsed = paexEddParseConfig(config);
  var now = paexEddWarehouseNow(parsed.tz);
  var today = new Date(Date.UTC(now.y, now.m - 1, now.d, 12, 0, 0));
  if (!paexEddIsShipDay(today, parsed)) return 0;
  var cutoffParts = parsed.cutoff.split(':');
  var cutoffSeconds = (Number(cutoffParts[0]) * 60 + Number(cutoffParts[1]) - (Number(bufferMinutes) || 0)) * 60;
  var nowSeconds = now.hour * 3600 + now.minute * 60 + (now.second || 0);
  return Math.max(0, (cutoffSeconds - nowSeconds) * 1000);
}

// Live-cache slot: the warehouse date plus the half of the day relative to the
// (default noon) cutoff. Keying every live cache on it means a date computed
// before the cutoff can never be served after it (Halloween clock 2026-10-09:
// a morning answer used to survive ~30-60 min past noon).
function paexEddLiveSlot() {
  var now = paexEddWarehouseNow('America/Toronto');
  var cutoff = String(window.paexEddCutoff || '12:00');
  var parts = /^(\d{1,2}):(\d{2})$/.exec(cutoff) || [0, 12, 0];
  var before = now.hour * 60 + now.minute < Number(parts[1]) * 60 + Number(parts[2]);
  return paexEddKey(now.y, now.m, now.d) + (before ? 'a' : 'p');
}

// Theme holiday list for the proxy (window.paexEddHolidays, set site-wide by
// snippets/paex-geotargetly.liquid): the worker merges these with its own
// HOLIDAYS var so live carrier dates skip the same closed days as the theme.
function paexEddLiveHolidayParam() {
  var list = String(window.paexEddHolidays || '')
    .split(/[\s,]+/)
    .filter(function (key) { return /^\d{4}-\d{2}-\d{2}$/.test(key); })
    .slice(0, 25);
  return list.length ? '&hol=' + list.join(',') : '';
}

// Theme cutoff for the proxy (Sasha 2026-10-26): the worker rolls the pickup
// day AND sets the carrier pickup time-of-day from it, so the customizer's
// "Same-day dispatch cutoff" is the single source of truth for live dates
// (window.paexEddCutoff, set site-wide by snippets/paex-geotargetly.liquid).
function paexEddLiveCutoffParam() {
  var cutoff = String(window.paexEddCutoff || '').trim();
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(cutoff) ? '&cutoff=' + cutoff : '';
}

function paexEddKey(y, m, d) {
  return (
    String(y) + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0')
  );
}

// Date-only arithmetic on UTC noon — immune to DST edges.
function paexEddAddDays(date, days) {
  var next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function paexEddIsShipDay(date, parsed) {
  var weekday = date.getUTCDay();
  if (parsed.days.indexOf(weekday) === -1) return false;
  var key = paexEddKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  return !parsed.holidays[key];
}

// The next ship day strictly after `date` (non-dispatch days + holidays skipped).
function paexEddNextShipDay(date, parsed) {
  var next = paexEddAddDays(date, 1);
  var guard = 0;
  while (!paexEddIsShipDay(next, parsed) && guard < 30) {
    next = paexEddAddDays(next, 1);
    guard += 1;
  }
  return next;
}

// `date` advanced by n ship days (n = 0 returns the same instant).
function paexEddAdvanceShipDays(date, n, parsed) {
  var probe = date;
  for (var i = 0; i < n; i += 1) probe = paexEddNextShipDay(probe, parsed);
  return probe;
}

// Arrival for a dispatch date: transit counts business days after dispatch;
// weekends/holidays don't deliver.
function paexEddArrivalFor(dispatchDate, transitDays, parsed) {
  var arrival = new Date(dispatchDate.getTime());
  var remaining = Math.max(1, transitDays);
  var guardDays = 0;
  while (remaining > 0 && guardDays < 60) {
    arrival = paexEddAddDays(arrival, 1);
    guardDays += 1;
    if (paexEddIsShipDay(arrival, parsed)) remaining -= 1;
  }
  return arrival;
}

// The full estimate for a destination province.
// Returns null only when the region has no transit row AND no "*" fallback.
// {
//   shipsToday (before cutoff AND zero processing), beforeCutoff (the
//   warehouse still takes orders for today's day zero), processingDays,
//   orderDay (Date, UTC-noon — day zero), dispatchDate (Date, UTC-noon —
//   carrier pickup = orderDay + processing ship days), arrival (Date,
//   UTC-noon), arrivalKey "YYYY-MM-DD", msToCutoff (>0 only when
//   beforeCutoff), transitDays
// }
function paexEddEstimate(regionCode, config) {
  var parsed = paexEddParseConfig(config);
  var region = String(regionCode || '').trim().toUpperCase();
  var transitDays = Object.prototype.hasOwnProperty.call(parsed.transit, region)
    ? parsed.transit[region]
    : parsed.transit['*'];
  if (transitDays == null) return null;

  var now = paexEddWarehouseNow(parsed.tz);
  var today = new Date(Date.UTC(now.y, now.m - 1, now.d, 12, 0, 0));
  var cutoffParts = parsed.cutoff.split(':');
  var cutoffMinutes = Number(cutoffParts[0]) * 60 + Number(cutoffParts[1]);
  var nowMinutes = now.hour * 60 + now.minute;

  // Day zero: today while the warehouse still takes orders for today's
  // dispatch (a ship day, before the cutoff), otherwise the next ship day.
  var beforeCutoff = paexEddIsShipDay(today, parsed) && nowMinutes < cutoffMinutes;
  var orderDay = beforeCutoff ? today : paexEddNextShipDay(today, parsed);
  // Processing days (Sasha 2026-09-21) push the carrier pickup past day zero;
  // "Ships today" is only true with zero processing.
  var dispatchDate = paexEddAdvanceShipDays(orderDay, parsed.processing, parsed);
  var shipsToday = beforeCutoff && parsed.processing === 0;

  var arrival = paexEddArrivalFor(dispatchDate, transitDays, parsed);
  var msToCutoff = beforeCutoff ? (cutoffMinutes - nowMinutes) * 60000 : 0;
  return {
    shipsToday: shipsToday,
    beforeCutoff: beforeCutoff,
    processingDays: parsed.processing,
    orderDay: orderDay,
    dispatchDate: dispatchDate,
    arrival: arrival,
    arrivalKey: paexEddKey(
      arrival.getUTCFullYear(), arrival.getUTCMonth() + 1, arrival.getUTCDate()
    ),
    msToCutoff: msToCutoff,
    transitDays: transitDays,
  };
}

// "Wed, Oct 22" — storefront-locale short date from a UTC-noon date.
function paexEddFormatDate(date, noWeekday) {
  try {
    var locale = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en';
    var options = noWeekday
      ? { month: 'short', day: 'numeric', timeZone: 'UTC' }
      : { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' };
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (error) {
    return date.toUTCString().slice(0, 11);
  }
}

// "6 h 41 m" from milliseconds (floored to the minute, never negative).
function paexEddFormatCountdown(ms) {
  var minutes = Math.max(0, Math.floor(ms / 60000));
  var hours = Math.floor(minutes / 60);
  return hours + ' h ' + String(minutes % 60).padStart(2, '0') + ' m';
}

// Sanity gate for a carrier-live date: the parcel cannot arrive on or before
// the day the carrier picks it up. A live key that early means the proxy did
// not account for the same dispatch/processing as the theme (stale worker,
// cache from before a redeploy) — reject it so the transit-table estimate
// stands and "Arrives" can never contradict "Ships {day}" (Sasha 2026-09-21).
var paexEddLiveWarned = paexEddLiveWarned || {};
function paexEddLiveAccepts(base, key) {
  if (!base || !base.dispatchDate || typeof base.dispatchDate.getUTCFullYear !== 'function') return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(key || ''))) return false;
  var dispatchKey = paexEddKey(
    base.dispatchDate.getUTCFullYear(), base.dispatchDate.getUTCMonth() + 1, base.dispatchDate.getUTCDate()
  );
  if (key > dispatchKey) return true;
  if (!paexEddLiveWarned[key] && window.console && console.warn) {
    paexEddLiveWarned[key] = true;
    console.warn('[paex-edd] live date ' + key + ' is not after the dispatch day ' + dispatchKey +
      ' — ignoring it (proxy out of step with processing days? redeploy proxy/paex-edd-worker.js); showing the transit-table date');
  }
  return false;
}

// LIVE provider (AfterShip via the server-side app proxy — the key never
// ships client-side). Contract: GET {proxyPath}/predict?dest_postal=XXX
// [&processing_days=N] -> 200 {date:"YYYY-MM-DD"} | 204. processing_days is
// omitted at 0 so the URL and caches stay byte-identical to the v1 contract. Session-cached 30 min per postal; resolves
// null on any miss/timeout so the transit estimate stands (the page never
// blocks — design 1g).
// City-mode: when no postal-grade destination exists at all, city + region
// still gets a live prediction (API-verified 2026-07-28: "Mirabel"+QC answers)
function paexEddLiveFetchCity(proxyPath, city, region, country, processingDays) {
  var cleanCity = String(city || '').trim().slice(0, 48);
  var cleanRegion = String(region || '').trim().toUpperCase();
  if (cleanCity.length < 2 || !/^[A-Z]{2}$/.test(cleanRegion)) return Promise.resolve(null);
  var cityProcessing = parseInt(processingDays, 10);
  if (!(cityProcessing > 0)) cityProcessing = 0;
  if (cityProcessing > 30) cityProcessing = 30;
  var cacheKey = 'paex:eddlive:c:' + cleanRegion + ':' + cleanCity.toUpperCase() +
    (cityProcessing ? ':p' + cityProcessing : '') + ':' + paexEddLiveSlot();
  try {
    var raw = sessionStorage.getItem(cacheKey);
    if (raw) {
      var hit = JSON.parse(raw);
      if (hit && Date.now() - hit.t < 1800000) return Promise.resolve(/^\d{4}-\d{2}-\d{2}$/.test(hit.k) ? hit.k : null);
    }
  } catch (error) { /* cache unreadable — fetch */ }
  var params = 'dest_city=' + encodeURIComponent(cleanCity) +
    '&dest_region=' + encodeURIComponent(cleanRegion) +
    '&dest_country=' + encodeURIComponent(String(country || 'CA').toUpperCase()) +
    (cityProcessing ? '&processing_days=' + cityProcessing : '') + paexEddLiveHolidayParam() + paexEddLiveCutoffParam();
  var cityBase = String(proxyPath || '').replace(/\/$/, '');
  var cityController = typeof AbortController === 'function' ? new AbortController() : null;
  if (cityController) setTimeout(function () { cityController.abort(); }, 3000);
  return fetch(cityBase + '/predict?' + params, {
    signal: cityController ? cityController.signal : undefined,
  })
    .then(function (response) { return response.ok ? response.json() : null; })
    .then(function (data) {
      var key = data && /^\d{4}-\d{2}-\d{2}$/.test(data.date) ? data.date : null;
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ k: key, t: Date.now() })); } catch (error) {}
      return key;
    })
    .catch(function () {
      console.warn('PAEX EDD: live city lookup failed — merchant transit table is answering.');
      return null;
    });
}

var paexEddLiveInflight = paexEddLiveInflight || {};

// Full live answer { date, max } (Halloween clock 2026-10-09: the worker
// always sent AfterShip's latest date too; "arrives before" decisions must use
// the latest, never the most optimistic). Session-cached per destination +
// processing + cutoff slot; identical in-flight requests share one fetch.
function paexEddLiveFetchFull(proxyPath, postal, processingDays) {
  var clean = String(postal || '').replace(/\s+/g, '').toUpperCase();
  // CA full postal, CA bare FSA (geo-IP grade, API-verified), or US ZIP
  if (!/^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(clean) && !/^\d{5}(-\d{4})?$/.test(clean)) {
    return Promise.resolve(null);
  }
  var processing = parseInt(processingDays, 10);
  if (!(processing > 0)) processing = 0;
  if (processing > 30) processing = 30;
  var cacheKey = 'paex:eddlive:' + clean + (processing ? ':p' + processing : '') + ':' + paexEddLiveSlot();
  try {
    var raw = sessionStorage.getItem(cacheKey);
    if (raw) {
      var hit = JSON.parse(raw);
      // misses (204 / error / timeout) are remembered too — 5 min — so a
      // postal the proxy can't answer is not re-requested on every page
      if (hit && hit.t && Date.now() - hit.t < (hit.k ? 1800000 : 300000)) {
        return Promise.resolve(/^\d{4}-\d{2}-\d{2}$/.test(hit.k)
          ? { date: hit.k, max: /^\d{4}-\d{2}-\d{2}$/.test(hit.x || '') ? hit.x : null }
          : null);
      }
    }
  } catch (error) { /* cache unreadable */ }
  if (paexEddLiveInflight[cacheKey]) return paexEddLiveInflight[cacheKey];

  var base = String(proxyPath || '').replace(/\/$/, '');
  var controller = typeof AbortController === 'function' ? new AbortController() : null;
  if (controller) setTimeout(function () { controller.abort(); }, 3000);
  var request = fetch(base + '/predict?dest_postal=' + encodeURIComponent(clean) +
    (processing ? '&processing_days=' + processing : '') + paexEddLiveHolidayParam() + paexEddLiveCutoffParam(), {
    signal: controller ? controller.signal : undefined,
  })
    .then(function (response) {
      if (!response.ok || response.status === 204) {
        if (!response.ok && window.console && console.warn) {
          // QA affordance: live EDD is ON but the proxy isn't answering —
          // the visible dates are the transit-table fallback
          console.warn('[paex-edd] live EDD enabled but proxy returned ' + response.status + ' at ' + base + ' — showing transit-table fallback (deploy the worker + app proxy, see proxy/README-EDD-PROXY.md)');
        }
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ k: null, t: Date.now() })); } catch (error) {}
        return null;
      }
      return response.json().then(function (data) {
        var key = data && /^\d{4}-\d{2}-\d{2}$/.test(String(data.date || '')) ? data.date : null;
        var max = key && /^\d{4}-\d{2}-\d{2}$/.test(String(data.max || '')) ? data.max : null;
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ k: key, x: max, t: Date.now() }));
        } catch (error) { /* cache unavailable */ }
        return key ? { date: key, max: max } : null;
      });
    })
    .catch(function () {
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ k: null, t: Date.now() })); } catch (error) {}
      return null;
    })
    .then(function (result) {
      delete paexEddLiveInflight[cacheKey];
      return result;
    });
  paexEddLiveInflight[cacheKey] = request;
  return request;
}

// Legacy contract (PDP block, cart strip, shipping page): the date only.
function paexEddLiveFetch(proxyPath, postal, processingDays) {
  return paexEddLiveFetchFull(proxyPath, postal, processingDays).then(function (result) {
    return result ? result.date : null;
  });
}

// LIVE provider B — the AfterShip EDD APP's own storefront API (the same
// call its widget makes from the browser; shop-keyed, no secret, CORS-open).
// Requires the "AfterShip Estimated Delivery Date" app to be INSTALLED.
// Works even without a postal (their backend IP-geolocates). Resolves
// null on any miss so the transit estimate stands (design 1g).
function paexEddAppFetch(config) {
  // app_key = the AfterShip EDD widget app key (theme setting paex_edd_app_key,
  // published site-wide as window.paexEddAppKey). The shop domain is NOT a
  // valid key — the API answers "Organization id is not found" (2026-10-04).
  var shop = String(config.appKey || window.paexEddAppKey || config.shop || '');
  var productId = String(config.productId || '');
  var variantId = String(config.variantId || '');
  if (!shop || !productId || !variantId) return Promise.resolve(null);
  var postal = String(config.postal || '').trim();
  var cacheKey = 'paex:eddapp:' + variantId + ':' + (postal || config.region || 'ip') + ':' + paexEddLiveSlot();
  try {
    var raw = sessionStorage.getItem(cacheKey);
    if (raw) {
      var hit = JSON.parse(raw);
      if (hit && hit.t && Date.now() - hit.t < 1800000) return Promise.resolve(/^\d{4}-\d{2}-\d{2}$/.test(hit.k) ? hit.k : null);
    }
  } catch (error) { /* cache unreadable */ }

  var body = {
    app_key: shop,
    app_platform: 'shopify',
    variants: [{ external_product_id: productId, external_variant_id: variantId }],
    language_code: (window.Shopify && Shopify.locale) || 'en',
  };
  if (config.region) body.country_region_code = config.country || 'CA';
  if (postal) body.postal_code = postal;

  var controller = typeof AbortController === 'function' ? new AbortController() : null;
  if (controller) setTimeout(function () { controller.abort(); }, 4000);
  return fetch('https://bff-api.automizely.com/edd/v1/product-aedd', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: controller ? controller.signal : undefined,
  })
    .then(function (response) {
      if (!response.ok) {
        if (window.console && console.warn) {
          console.warn('[paex-edd] AfterShip app endpoint returned ' + response.status + ' — is the "AfterShip Estimated Delivery Date" app installed? Showing the transit-table fallback.');
        }
        return null;
      }
      return response.json().then(function (payload) {
        var items = payload && payload.data && payload.data.items;
        var item = items && items.length ? items[0] : null;
        var key = item && /^\d{4}-\d{2}-\d{2}/.test(String(item.estimated_delivery_date || ''))
          ? String(item.estimated_delivery_date).slice(0, 10)
          : null;
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ k: key, t: Date.now() }));
        } catch (error) { /* cache unavailable */ }
        return key;
      });
    })
    .catch(function () { return null; });
}

// Order-by date for an occasion: the LAST order day (a ship day, ordered
// before the cutoff) whose arrival — after processing + transit — lands on or
// before arriveBy ("YYYY-MM-DD"). Returns { orderBy (Date), key } or null when
// even ordering right now misses it. With zero processing the order day and
// the dispatch day coincide (the v1 behaviour).
function paexEddOrderBy(regionCode, config, arriveByKey) {
  var estimate = paexEddEstimate(regionCode, config);
  if (!estimate || !arriveByKey) return null;
  if (estimate.arrivalKey > arriveByKey) return null;

  var parsed = paexEddParseConfig(config);
  var probe = estimate.orderDay;
  var last = probe;
  // Walk forward one order day at a time until the arrival misses the
  // occasion. 370-step outer guard = occasions up to ~a year out; the inner
  // skip loops carry their own guards.
  var steps = 0;
  while (steps < 370) {
    steps += 1;
    var next = paexEddNextShipDay(probe, parsed);
    var dispatch = paexEddAdvanceShipDays(next, parsed.processing, parsed);
    var arrival = paexEddArrivalFor(dispatch, estimate.transitDays, parsed);
    var arrivalKey = paexEddKey(
      arrival.getUTCFullYear(), arrival.getUTCMonth() + 1, arrival.getUTCDate()
    );
    if (arrivalKey > arriveByKey) break;
    probe = next;
    last = probe;
  }
  return { orderBy: last, key: paexEddKey(
    last.getUTCFullYear(), last.getUTCMonth() + 1, last.getUTCDate()
  ) };
}
